import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery } from '../../services/baseQuery'
import { Patient } from '../../types'
import { CreatePatientRequest } from '../../utils/patient/patientPayload'

export type EhrPatientUnavailableReason =
  | 'no_patient_context'
  | 'authorization'
  | 'not_found'
  | 'rate_limited'
  | 'upstream'

export type EhrPatientResolution =
  | { status: 'matched'; patient: Patient }
  | { status: 'selection_required'; candidates: Patient[] }
  | { status: 'registration_required'; draft: Partial<CreatePatientRequest> }
  | { status: 'unavailable'; reason: EhrPatientUnavailableReason; retryable: boolean }

export type EhrMedicationRequestStatus =
  | 'active'
  | 'on-hold'
  | 'cancelled'
  | 'completed'
  | 'entered-in-error'
  | 'stopped'
  | 'draft'
  | 'unknown'

export interface EhrRecentMedicationRequest {
  name: string
  authoredOn: string | null
  status: EhrMedicationRequestStatus
  represcribing: EhrReprescribingProjection
}

export type EhrReprescribingEligibility =
  | 'eligible'
  | 'entered_in_error'
  | 'not_an_order'
  | 'do_not_perform'
  | 'medicine_unavailable'

export interface EhrReprescribingPrefill {
  patientInstructions?: string
  numberOfRepeatsAuthorised?: number
  brandSubstitutionNotAllowed?: boolean
  reasonForPrescribing?: string
}

export interface EhrReprescribingProjection {
  eligibility: EhrReprescribingEligibility
  medicineCoding?: {
    system: 'http://snomed.info/sct'
    code: string
    display?: string
  }
  searchText?: string
  prefill?: EhrReprescribingPrefill
}

export type EhrRecentMedicationRequestsResult =
  | { status: 'available'; medications: EhrRecentMedicationRequest[] }
  | { status: 'unavailable' }

export const ehrLaunchPatientApi = createApi({
  reducerPath: 'ehrLaunchPatientApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/auth/ehr-launch' }),
  endpoints: (build) => ({
    resolveLaunchPatient: build.mutation<EhrPatientResolution, void>({
      query: () => ({ url: '/resolve-patient', method: 'post' }),
    }),
    retrieveRecentMedicationRequests: build.mutation<EhrRecentMedicationRequestsResult, void>({
      query: () => ({ url: '/recent-medication-requests', method: 'post' }),
    }),
  }),
})

export const { useResolveLaunchPatientMutation, useRetrieveRecentMedicationRequestsMutation } =
  ehrLaunchPatientApi
